import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Artist } from '@/types/models'
import {
  CITIES_ALLOWED_UNDER_MIN_ELIGIBLE_ARTISTS,
  cityPassesEligibleArtistThreshold,
} from '@/lib/marketCities'
import { countryNameFromSupportedMarketCity, normalizePlainCityToken } from '@/lib/locationRegions'

/**
 * Public listing (Artists UI + backend feed parity): hide only explicit `isAvailable: false`.
 * Matches backend `artist_data.get('isAvailable') is not False`.
 */
export function isArtistEligibleForPublicListing(artist: Pick<Artist, 'isAvailable'>): boolean {
  return artist.isAvailable !== false
}

function cityAlreadyInList(citiesArray: string[], catalogCity: string): boolean {
  const target = normalizePlainCityToken(catalogCity).toLowerCase()
  return citiesArray.some((c) => normalizePlainCityToken(c).toLowerCase() === target)
}

function countryFromArtistSamples(
  citySampleCountry: Map<string, string>,
  canon: string,
): string | undefined {
  const n = normalizePlainCityToken(canon).toLowerCase()
  for (const [c, co] of citySampleCountry.entries()) {
    if (normalizePlainCityToken(c).toLowerCase() === n) return co
  }
  return undefined
}

interface ArtistsData {
  allArtists: Artist[]
  availableArtists: Artist[]
  availableArtistIds: Set<string>
  cities: string[]
  /** Eligible artist count per launched filter city (from the single artists collection load). */
  cityArtistCountByCity: Record<string, number>
  /** Country name per city (from artist profiles) for flags in pickers */
  cityCountryByCity: Record<string, string>
  loading: boolean
  error: Error | null
}

const ArtistsDataContext = createContext<ArtistsData | undefined>(undefined)

export function ArtistsDataProvider({ children }: { children: ReactNode }) {
  const [allArtists, setAllArtists] = useState<Artist[]>([])
  const [availableArtists, setAvailableArtists] = useState<Artist[]>([])
  const [availableArtistIds, setAvailableArtistIds] = useState<Set<string>>(new Set())
  const [cities, setCities] = useState<string[]>([])
  const [cityArtistCountByCity, setCityArtistCountByCity] = useState<Record<string, number>>({})
  const [cityCountryByCity, setCityCountryByCity] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchArtistsData = async () => {
      try {
        setLoading(true)
        console.log('[ArtistsDataContext] Fetching artists data (single request)')
        
        const querySnapshot = await getDocs(collection(db, 'artists'))
        
        const all: Artist[] = []
        const available: Artist[] = []
        const availableIds = new Set<string>()
        const cityCounts = new Map<string, number>()
        const citySampleCountry = new Map<string, string>()
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data()
          const artist: Artist = {
            id: doc.id,
            firstName: data.firstName,
            lastName: data.lastName,
            displayName: data.displayName || 'Unknown Artist',
            avatarUrl: data.avatarUrl,
            location: data.location,
            isAvailable: data.isAvailable,
            bio: data.bio,
            styles: data.styles || [], // Add styles array
            createdAt: data.createdAt, // Add createdAt
          }
          
          all.push(artist)
          
          if (isArtistEligibleForPublicListing(artist)) {
            available.push(artist)
            availableIds.add(doc.id)
            
            const city = artist.location?.city
            const country = artist.location?.country
            if (city && !city.includes('_')) {
              cityCounts.set(city, (cityCounts.get(city) || 0) + 1)
              if (country && !citySampleCountry.has(city)) {
                citySampleCountry.set(city, country)
              }
            }
          }
        })
        
        const artistCountByCity: Record<string, number> = {}
        for (const [city, count] of cityCounts.entries()) {
          if (!cityPassesEligibleArtistThreshold(city, count)) continue
          const canon = normalizePlainCityToken(city)
          artistCountByCity[canon] = (artistCountByCity[canon] ?? 0) + count
        }
        let citiesArray = Object.keys(artistCountByCity)

        const countryByCity: Record<string, string> = {}
        for (const city of citiesArray) {
          const fromArtist = countryFromArtistSamples(citySampleCountry, city)
          const regionCountry = countryNameFromSupportedMarketCity(city)
          if (fromArtist) countryByCity[city] = fromArtist
          else if (regionCountry) countryByCity[city] = regionCountry
        }

        for (const catalogCity of CITIES_ALLOWED_UNDER_MIN_ELIGIBLE_ARTISTS) {
          const canon = normalizePlainCityToken(catalogCity)
          if (!cityAlreadyInList(citiesArray, canon)) {
            citiesArray.push(canon)
            artistCountByCity[canon] = cityCounts.get(catalogCity) ?? cityCounts.get(canon) ?? 0
            const fromArtist = countryFromArtistSamples(citySampleCountry, canon)
            const regionCountry = countryNameFromSupportedMarketCity(canon)
            if (fromArtist) countryByCity[canon] = fromArtist
            else if (regionCountry) countryByCity[canon] = regionCountry
          }
        }
        citiesArray.sort((a, b) => {
          const countDiff = (artistCountByCity[b] ?? 0) - (artistCountByCity[a] ?? 0)
          if (countDiff !== 0) return countDiff
          return a.localeCompare(b)
        })
        
        setAllArtists(all)
        setAvailableArtists(available)
        setAvailableArtistIds(availableIds)
        setCities(citiesArray)
        setCityArtistCountByCity(artistCountByCity)
        setCityCountryByCity(countryByCity)
        
        console.log('[ArtistsDataContext] Loaded:', {
          totalArtists: all.length,
          availableArtists: available.length,
          cities: citiesArray.length
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        const errorCode = (err as any)?.code || 'UNKNOWN'
        const fullError = new Error(`[ArtistsDataContext] Failed to fetch artists - Code: ${errorCode}, Message: ${errorMessage}`)
        console.error(fullError)
        console.error('[ArtistsDataContext] Full error object:', err)
        setError(fullError)
      } finally {
        setLoading(false)
      }
    }

    fetchArtistsData()
  }, [])

  const value: ArtistsData = {
    allArtists,
    availableArtists,
    availableArtistIds,
    cities,
    cityArtistCountByCity,
    cityCountryByCity,
    loading,
    error
  }

  return (
    <ArtistsDataContext.Provider value={value}>
      {children}
    </ArtistsDataContext.Provider>
  )
}

export function useArtistsData(): ArtistsData {
  const context = useContext(ArtistsDataContext)
  if (context === undefined) {
    throw new Error('useArtistsData must be used within an ArtistsDataProvider')
  }
  return context
}

